import {
  ALTURA_SAIDA,
  ESCALA,
  FAMILIA_FONTE,
  FUNDO_PADRAO,
  LARGURA_SAIDA,
  PALETA_SERIES,
  PLOT_FUNDO_PADRAO,
  ROTULO_PADRAO,
  TEXTO_PADRAO,
  ESTILO_PADRAO,
  comAlfa,
  fontesEscaladas,
  formatarValor,
  gradienteCilindro,
  gradienteVertical,
  montarTextoSegmentado,
} from "../tema";
import { desenharCamadas } from "../desenho";

/**
 * Opções comuns a todas as famílias: fundo, tipografia, cabeçalho de três
 * linhas (título / subtítulo / fonte) e legenda.
 *
 * O cabeçalho das referências tem até três linhas, mas o Highcharts só
 * oferece title e subtitle — por isso a linha "Fonte: …" entra no próprio
 * subtitle depois de um <br>, que vira uma segunda linha de <tspan> no SVG
 * (e portanto sobrevive à exportação, ao contrário de useHTML).
 */
export function opcoesBase(spec = {}) {
  // Todos os tamanhos passam pela escala (manual + automatica), para que
  // uma serie longa caiba sem precisar mexer em cada familia.
  const FONTES = fontesEscaladas(spec);
  const estilo = { ...ESTILO_PADRAO, ...(spec.estilo || {}) };
  const moderno = estilo.visual !== "classico";
  const legendaCfg = spec.legenda || {};
  const temSubtitulo = Array.isArray(spec.subtitulo)
    ? spec.subtitulo.some((s) => s?.texto)
    : Boolean(spec.subtitulo);
  const temFonte = Boolean(spec.fonte);

  const linhasSubtitulo = [];
  if (temSubtitulo) linhasSubtitulo.push(montarTextoSegmentado(spec.subtitulo));
  if (temFonte) {
    linhasSubtitulo.push(
      `<span style="color:${TEXTO_PADRAO};font-size:${FONTES.fonteCredito}px">${spec.fonte}</span>`
    );
  }

  // NÃO definir chart.marginTop aqui. Com uma margem superior explícita o
  // Highcharts passa a tratar a legenda como flutuante e a desenha DENTRO
  // da área de plotagem, em cima das colunas. Deixando o cálculo com ele,
  // o espaço de título + subtítulo + legenda é reservado corretamente.
  return {
    // O spec viaja dentro das opcoes para que o handler de desenho o
    // encontre tambem na copia que o getSVG monta na hora de exportar.
    rbSpec: spec,
    chart: {
      backgroundColor: spec.fundo || FUNDO_PADRAO,
      // "parede" atrás das colunas: nas referências é um degradê
      // azul-ardósia. `fundoPlot: "nenhum"` deixa tudo preto, que é como
      // as famílias de linha e de ranking aparecem.
      plotBackgroundColor:
        spec.fundoPlot === "nenhum"
          ? null
          : spec.fundoPlot || PLOT_FUNDO_PADRAO,
      width: LARGURA_SAIDA,
      height: ALTURA_SAIDA,
      marginBottom: 19 * ESCALA,
      marginLeft: 5 * ESCALA,
      marginRight: 5 * ESCALA,
      spacingTop: 4 * ESCALA,
      // Só usado quando a imagem cobre apenas a área de plotagem. Cobrindo
      // o quadro inteiro (o padrão), quem desenha é o desenharFundo, porque
      // o Highcharts não tem opção de imagem para o gráfico todo.
      // Sempre data URI: um SVG rasterizado dentro de <img> não carrega URL
      // externa e ainda contaminaria o canvas, quebrando a exportação.
      plotBackgroundImage:
        (spec.fundoImagem?.abrangencia ?? "tudo") === "grafico"
          ? spec.fundoImagem?.dataUri || null
          : null,
      style: { fontFamily: FAMILIA_FONTE },
      animation: false,
      // Um unico handler cuida de tudo que e desenhado a mao: a camada
      // translucida sobre a imagem de fundo, as setas de variacao e as
      // caixas de texto. Fica em events.render porque o getSVG() monta um
      // grafico NOVO a partir destas opcoes — desenho feito de fora
      // existiria so no preview e sumiria da imagem exportada.
      events: { render: desenharCamadas },
    },
    title: {
      text: montarTextoSegmentado(spec.titulo),
      useHTML: false,
      // O padrão é -44, reservado para o botão de contexto do menu de
      // exportação — que aqui está desligado. Sem zerar, títulos longos
      // quebram ou ganham reticências 44px antes da borda.
      widthAdjust: 0,
      style: {
        color: TEXTO_PADRAO,
        fontSize: `${FONTES.titulo}px`,
        fontWeight: "bold",
      },
    },
    subtitle: {
      text: linhasSubtitulo.join("<br>"),
      useHTML: false,
      widthAdjust: 0,
      style: {
        color: TEXTO_PADRAO,
        fontSize: `${FONTES.subtitulo}px`,
        fontWeight: "bold",
      },
    },
    legend: {
      enabled: Boolean(spec.legenda?.visivel),
      align: "center",
      verticalAlign: "top",
      y: (temSubtitulo && temFonte ? 14 : 4) * ESCALA,
      // No visual moderno a legenda ganha uma pastilha de vidro: fundo
      // translucido, borda tenue e cantos arredondados. No classico ela
      // fica solta sobre o fundo, como nas referencias antigas.
      padding: moderno ? 5 * ESCALA : 0,
      margin: 6 * ESCALA,
      backgroundColor: moderno
        ? comAlfa(legendaCfg.corFundo || "#FFFFFF", legendaCfg.opacidade ?? 0.1)
        : undefined,
      borderColor: moderno ? comAlfa(legendaCfg.corBorda || "#FFFFFF", 0.22) : undefined,
      borderWidth: moderno ? 1 : 0,
      borderRadius: moderno ? 10 * ESCALA : 0,
      symbolHeight: 9 * ESCALA,
      symbolWidth: 9 * ESCALA,
      symbolRadius: moderno ? 3 : 0,
      itemDistance: 14 * ESCALA,
      itemStyle: {
        color: legendaCfg.corTexto || TEXTO_PADRAO,
        fontSize: `${FONTES.legenda}px`,
        fontWeight: "bold",
      },
      itemHoverStyle: { color: legendaCfg.corTexto || TEXTO_PADRAO },
    },
    credits: { enabled: false },
    // sourceWidth/Height é o que o getSVG() consulta para decidir em que
    // tamanho re-renderizar: fixar aqui garante a saída exata mesmo que o
    // preview na tela esteja em outra escala.
    exporting: {
      enabled: false,
      sourceWidth: LARGURA_SAIDA,
      sourceHeight: ALTURA_SAIDA,
      buttons: { contextButton: { enabled: false } },
    },
    // O módulo de acessibilidade não é importado; sem desligar, cada
    // gráfico despeja um aviso no console.
    accessibility: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      series: {
        animation: false,
        states: { hover: { enabled: false }, inactive: { opacity: 1 } },
      },
    },
    xAxis: {
      lineColor: "#555555",
      tickLength: 0,
      // Em 3D o Highcharts desenha as linhas de grade na parede do fundo,
      // o que vira um gradeado branco que as referências não têm.
      gridLineWidth: 0,
      labels: {
        style: {
          color: spec.eixo?.corTexto || TEXTO_PADRAO,
          fontSize: `${FONTES.categoria}px`,
          fontWeight: "bold",
        },
      },
    },
    yAxis: {
      visible: false,
      title: { text: null },
      gridLineWidth: 0,
      // Começar no zero achata comparações de valores próximos: entre
      // 2,470 e 2,593 a diferença some. As referências cortam a base
      // justamente para deixá-la visível — daí o mínimo ser configurável.
      min: spec.eixo?.min ?? null,
      max: spec.eixo?.max ?? null,
      // Espaço extra no topo para a caixa do rótulo não encostar no
      // cabeçalho nem ser cortada pela borda da área de plotagem.
      maxPadding: 0.1,
    },
  };
}

/** Estilo da caixa de rótulo (texto claro sobre caixa escura, como nas referências). */
export function estiloRotulo(spec = {}) {
  const rotulo = { ...ROTULO_PADRAO, ...(spec.rotulo || {}) };
  const estilo = { ...ESTILO_PADRAO, ...(spec.estilo || {}) };
  const moderno = estilo.visual !== "classico";
  const fontes = fontesEscaladas(spec);
  const largura = rotulo.larguraBorda ?? 1;

  return {
    enabled: true,
    // No moderno a caixa vira vidro: o fundo deixa passar a coluna e a
    // borda fica tenue. Todos os valores continuam editaveis pelo usuario.
    // No clássico a caixa é sólida — a não ser que a opacidade tenha sido
    // ajustada no painel: aí ela vale nos dois visuais (antes o controle
    // aparecia no clássico e não fazia nada).
    backgroundColor: moderno
      ? comAlfa(rotulo.corFundo, rotulo.opacidade ?? estilo.translucidez)
      : rotulo.opacidade != null
      ? comAlfa(rotulo.corFundo, rotulo.opacidade)
      : rotulo.corFundo,
    borderColor: moderno ? comAlfa(rotulo.corBorda, estilo.contorno) : rotulo.corBorda,
    borderWidth: largura,
    borderRadius: moderno ? estilo.cantos : 2,
    padding: (rotulo.espaco ?? 3) * ESCALA,
    shadow: false,
    crop: false,
    overflow: "allow",
    style: {
      color: rotulo.corTexto,
      fontSize: `${fontes.rotulo}px`,
      fontWeight: "bold",
      textOutline: "none",
    },
  };
}

/**
 * Converte as séries do spec em séries do Highcharts, aplicando a paleta
 * padrão para as que não tiverem cor própria.
 */
export function montarSeries(spec = {}, extras = {}) {
  const series = spec.dados?.series || [];
  // No formato cilindro o volume vem do gradiente horizontal de três
  // paradas (borda escura / centro claro / borda escura), que é como as
  // referências são sombreadas.
  const cilindro = (spec.extras?.formato ?? "cilindro") === "cilindro";
  return series.map((serie, i) => {
    const padrao = PALETA_SERIES[i % PALETA_SERIES.length];
    const cor = serie.cor || padrao.cor;
    const corFim = serie.corFim || padrao.corFim;
    return {
      name: serie.nome,
      data: (serie.valores || []).map((v) => (v === null ? null : Number(v))),
      color: cilindro
        ? gradienteCilindro(cor, corFim)
        : gradienteVertical(cor, corFim),
      borderWidth: 0,
      ...extras,
    };
  });
}

/** Formatter de rótulo que respeita as opções de número do spec. */
export function formatterValor(spec = {}) {
  const opcoes = spec.valores || {};
  return function formatar() {
    return formatarValor(this.y, opcoes);
  };
}
